import { cx } from "@linaria/core";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { getElementContent } from "@pm2/api";
import type { PM2ComponentData, PageAST } from "@pm2/content-engine";
import {
  useContentEnginePrinters,
  inferUserAccessFromAST,
  parseElementXML,
  pm2CurrentAST,
} from "@pm2/content-engine";
import { verticalPaddingCls } from "@pm2/react-utils";
import { pm2ConfigStore, type PM2ConfigStore } from "@pm2/store";
import { EmptyPage, MissingAccessMessage, NotFound } from "@pm2/ui-components";
import type { UserIdentity } from "@provider/user-identity";
import { speechSelectors } from "@ui/kit";
import type { BaseNode } from "@utils/ast";
import { printAST } from "@utils/ast";
import { useKatex } from "@utils/react";

import { Helmet } from "../../server/utils/module.react-helmet-async";
import { joinDocumentTitle } from "../utils";

import * as styles from "./page.content.style";

type Props = {
  ast: PageAST | undefined;
};

export function ElementPage(props: Props): React.JSX.Element {
  const portalTitle = pm2ConfigStore.useSelector((state) => state.config.title);
  const containerRef = useRef<HTMLDivElement>(null);

  useKatex(containerRef, [props.ast]);

  const { printers, interceptor } = useContentEnginePrinters();

  const title = findLearningObjectTitleInAST(props.ast ?? []);
  const hasAccess = inferUserAccessFromAST(props.ast ?? []);
  const isPageEmpty = props.ast?.length === 0;

  useEffect(() => {
    pm2CurrentAST.current = props.ast;
  }, [props.ast]);

  if (!props.ast) {
    return <NotFound />;
  }

  if (isPageEmpty) {
    return <EmptyPage />;
  }

  const children = printAST(props.ast, printers, interceptor);

  return (
    <article
      ref={containerRef}
      className={cx(
        "gPortal-page",
        styles.articleCls,
        verticalPaddingCls,
        speechSelectors.enableSpeech.className,
      )}
    >
      <Helmet>
        <title>{joinDocumentTitle(title, portalTitle)}</title>
      </Helmet>

      {!hasAccess && <MissingAccessMessage />}
      {children}
    </article>
  );
}

/**
 * fetch and render the content of a stand-alone content element.
 */
export async function renderElementPage(
  queryClient: QueryClient,
  configStore: PM2ConfigStore,
  user: UserIdentity | undefined | null,
  elementId: string,
): Promise<React.JSX.Element> {
  const configState = configStore.getState();

  const xml = await getElementContent(configState.config.id, elementId, {
    previewMode: configState.previewMode,
  });
  const ast = xml ? await parseElementXML(queryClient, xml, user) : undefined;

  return <ElementPage ast={ast} />;
}

type ASTNode = BaseNode & { pm2?: PM2ComponentData; children?: ASTNode[] };

/**
 * recursively scan the provided content AST and find the first learning object
 * with a provided title
 */
function findLearningObjectTitleInAST(ast: ASTNode[]): string | undefined {
  return ast.find((node) => {
    if (node.pm2?.learningObjectId && node.pm2.learningObjectTitle) {
      return true;
    }

    if (node.children) {
      // recursively iterate over all children
      return findLearningObjectTitleInAST(node.children);
    }

    return false;
  })?.pm2?.learningObjectTitle;
}
